import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import HelpTooltipIcon from '../../../../components/HelpTooltipIcon'
import { CustomTooltip } from '../../../../components/charts/CustomTooltip'
import Dot from '../../../../icons/Dot'
import { useTheme } from '../../../../theme'
import { formatThousands } from '../../../../utils/formatNumber'
import { useCountersAnalyticsData } from '../../analytics/hooks/use-counters-analytics-data'

const MOVEMENT_COLORS = {
  walking: '#5664D2',
  cycling: '#43A047',
  rollers: '#FB8C00',
  rolling: '#E53935',
  scooting: '#8E24AA',
  jogging: '#00ACC1',
  skateboarding: '#F4511E',
} as const

const MovementStatsChart = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { movementStats } = useCountersAnalyticsData()

  const chartData =
    !movementStats || movementStats.total === 0
      ? [{ name: t('common.noData'), value: 1, fill: '#eeeeee' }]
      : Object.entries(MOVEMENT_COLORS)
          .map(([type, color]) => ({
            name: t(`activity.movementTypes.${type}`),
            value: movementStats[type as keyof typeof movementStats] || 0,
            fill: color,
          }))
          .filter((item) => item.value > 0)

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'visible',
      }}
    >
      <Box sx={{ display: 'flex', p: 2 }}>
        <Typography color='inherit' variant='body2' textTransform={'uppercase'}>
          {t('activity.movementStats')}
        </Typography>
        <Box sx={{ pl: 1 }}>
          <HelpTooltipIcon text={t('activity.tooltips.movementStats')} />
        </Box>
      </Box>
      <CardContent
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: { xs: '100%', sm: '50%' },
          }}
        >
          <ResponsiveContainer width={'100%'} height={250}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey='value'
                nameKey='name'
                cx='50%'
                cy='50%'
                innerRadius='75%'
                outerRadius='100%'
                startAngle={90}
                endAngle={-270}
                style={{ outline: 'none' }}
              />
              <Tooltip
                wrapperStyle={{ zIndex: 9999 }}
                content={CustomTooltip}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ width: { xs: '100%', sm: '50%' }, pl: 3 }}>
          <Typography variant={'h4'}>
            {formatThousands(movementStats?.total || 0)}
          </Typography>
          <Typography
            pt={1}
            pb={1}
            variant={'subtitle2'}
            fontWeight={'bold'}
            color={theme.palette.text.secondary}
          >
            {t('activity.totalMovementEvents')}
          </Typography>
          <List>
            {Object.entries(MOVEMENT_COLORS).map(([type, color]) => {
              const value =
                (movementStats?.[
                  type as keyof typeof movementStats
                ] as number) || 0
              if (value === 0) return null
              return (
                <ListItem
                  key={type}
                  divider
                  disablePadding
                  secondaryAction={
                    <ListItemText primary={formatThousands(value)} />
                  }
                >
                  <ListItemIcon>
                    <Dot sx={{ color, fontSize: 32 }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'subtitle2' }}
                    primary={t(`activity.movementTypes.${type}`)}
                  />
                </ListItem>
              )
            })}
          </List>
        </Box>
      </CardContent>
    </Card>
  )
}

export default MovementStatsChart
